import React from 'react';
import { FaPen, FaChevronDown, FaChevronRight } from 'react-icons/fa';
import { personalakteStyles as ps, colors } from '../styles/personalakteStyles';
import { EMPLOYMENT_TYPE_OPTIONS } from '../utils/constants';
import { CompensationType } from '../services/CompensationService';
import { formatDaysHalf } from '../utils/leaveFormat';
import { toNullableDecimalString, parseDecimalString } from '../utils/leaveHelpers';
import { CISelect } from './CISelect';
import { CompensationForm } from './CompensationForm';
import styles from './ContractHistoryTimeline.module.css';

const DAY_LABELS = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];

function formatDateDE(d: string | null | undefined): string {
  if (!d) return '\u2013';
  const date = new Date(d);
  if (isNaN(date.getTime())) return '\u2013';
  return date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function getEmploymentLabel(value: string | undefined): string {
  if (!value) return '\u2013';
  const opt = EMPLOYMENT_TYPE_OPTIONS.find(o => o.value === value);
  return opt ? opt.label : value;
}

const DataRow: React.FC<{ label: string; value: string; highlight?: boolean; highlightColor?: string }> = ({
  label, value, highlight, highlightColor,
}) => (
  <div className={styles.dataRow}>
    <span className={`${styles.dataRowLabel} ${highlight ? styles.dataRowLabelHighlight : ''}`}>{label}</span>
    <span
      className={styles.dataRowValue}
      style={highlight ? { color: highlightColor || colors.magenta, fontWeight: 700 } : undefined}
    >
      {value}
    </span>
  </div>
);

const DashedSeparator: React.FC = () => <div className={styles.dashedSeparator} />;

interface ContractPeriodCardProps {
  profile: any;
  startDate: string | null;
  endDate: string | null;
  positionTitle: string;
  department: string;
  employmentType: string | undefined;
  hoursPerWeek: number | null;
  days: number[];
  totalLeavePerYear: number | null;
  statutory: number | null;
  extra: number;
  proRata: number | null;
  monthsEmployed: number;
  year: number;
  leaveTaken: number | null;
  leaveRemaining: number | null;
  overtimeCarryover: number | null;
  compHistory: any[];
  compensation: any;
  compLocked: boolean;
  editingContract: boolean;
  showCompForm: boolean;
  compForm: { type: CompensationType; amount: string; valid_from: string };
  compSaving: boolean;
  workAreas: Array<{ id: number; name: string }>;
  onStartEditing: () => void;
  onCancelEditing: () => void;
  onSetShowCompForm: (show: boolean) => void;
  onCompFormChange: (form: { type: CompensationType; amount: string; valid_from: string }) => void;
  onSaveCompensation: () => void;
  onUpdateJob: (field: string, value: any) => void;
  onUpdateWorkArea: (waId: string) => void;
  onUpdateWorkingPattern: (field: string, value: any) => void;
  onLeaveChange?: (leave: any) => void;
}

export const ContractPeriodCard: React.FC<ContractPeriodCardProps> = ({
  profile, startDate, endDate, positionTitle, department, employmentType,
  hoursPerWeek, days, totalLeavePerYear, statutory, extra, proRata,
  monthsEmployed, year, leaveTaken, leaveRemaining, overtimeCarryover,
  compHistory, compensation, compLocked, editingContract, showCompForm,
  compForm, compSaving, workAreas, onStartEditing, onCancelEditing,
  onSetShowCompForm, onCompFormChange, onSaveCompensation,
  onUpdateJob, onUpdateWorkArea, onUpdateWorkingPattern, onLeaveChange,
}) => {
  if (!startDate && !editingContract) return null;

  const regularWdDisplay: number[] = profile?.working_pattern?.regular_workdays
    ?? (days.length === 7 ? days.map((d: number) => d > 0 ? 1 : 0) : []);
  const wdCountDisplay = regularWdDisplay.filter((v: number) => v > 0).length;

  return (
    <div className={styles.timelineEntry}>
      <div className={styles.activeDot} />

      <div className={styles.activeDateRow}>
        <span className={styles.activeDate}>seit {formatDateDE(startDate)}</span>
        <span className={styles.activeBadge}>AKTIV</span>
        {!editingContract && (
          <button type="button" onClick={onStartEditing} className={styles.editButton}>
            <FaPen size={10} /> Bearbeiten
          </button>
        )}
      </div>

      <div className={styles.contractCard}>
        {editingContract ? (
          <div className={styles.editForm}>
            <div className={styles.formGrid2}>
              <div className="pa-field" style={ps.fieldWrapper}>
                <label style={ps.labelStyle}>Vertragsbeginn</label>
                <input type="date" style={ps.inputStyle} value={startDate || ''} onChange={e => onUpdateJob('start_date', e.target.value)} />
              </div>
              <div className="pa-field" style={ps.fieldWrapper}>
                <label style={ps.labelStyle}>Vertragsende</label>
                <input type="date" style={ps.inputStyle} value={endDate || ''} onChange={e => onUpdateJob('contract_end_date', e.target.value || null)} />
              </div>
            </div>
            <div className="pa-field" style={ps.fieldWrapper}>
              <label style={ps.labelStyle}>Rolle</label>
              <input type="text" style={ps.inputStyle} value={profile?.job?.position_title || ''} onChange={e => onUpdateJob('position_title', e.target.value)} />
            </div>
            <div className={styles.formGrid2}>
              <div className="pa-field" style={ps.fieldWrapper}>
                <label style={ps.labelStyle}>Arbeitsbereich</label>
                <CISelect
                  value={profile?.job?.work_areas_detail?.[0]?.id ? String(profile.job.work_areas_detail[0].id) : (profile?.job?.primary_work_area ? String(profile.job.primary_work_area) : '')}
                  onChange={onUpdateWorkArea}
                  options={workAreas.map(wa => ({ value: String(wa.id), label: wa.name }))}
                />
              </div>
              <div className="pa-field" style={ps.fieldWrapper}>
                <label style={ps.labelStyle}>Beschaeftigungsart</label>
                <CISelect value={employmentType || ''} onChange={val => onUpdateJob('employment_type', val)} options={[...EMPLOYMENT_TYPE_OPTIONS]} />
              </div>
            </div>
            <div className="pa-field" style={ps.fieldWrapper}>
              <label style={ps.labelStyle}>Stunden/Woche</label>
              <input type="number" style={ps.inputStyle} value={hoursPerWeek ?? ''} onChange={e => onUpdateWorkingPattern('hours_per_week', e.target.value ? parseFloat(e.target.value) : null)} step="0.5" min="0" max="48" />
            </div>
            {/* Arbeitstage toggles */}
            <div className="pa-field" style={ps.fieldWrapper}>
              <label style={ps.labelStyle}>Arbeitstage</label>
              {(() => {
                const regularWd: number[] = profile?.working_pattern?.regular_workdays
                  ?? (days.length === 7 ? days.map((d: number) => d > 0 ? 1 : 0) : [0, 0, 0, 0, 0, 0, 0]);
                const wdCount = regularWd.filter((v: number) => v > 0).length;
                return (
                  <div>
                    <div className={styles.dayToggles}>
                      {DAY_LABELS.map((day, i) => {
                        const active = regularWd[i] > 0;
                        return (
                          <button key={day} type="button" onClick={() => { const updated = [...regularWd]; updated[i] = active ? 0 : 1; onUpdateWorkingPattern('regular_workdays', updated); }}
                            className={`${styles.dayToggle} ${active ? styles.dayToggleActive : styles.dayToggleInactive}`}>
                            {day}
                          </button>
                        );
                      })}
                    </div>
                    <div className={styles.dayCount}>{wdCount} Tage/Woche</div>
                  </div>
                );
              })()}
            </div>
            {/* Leave */}
            <div className={styles.formGrid3}>
              <div className="pa-field" style={{ ...ps.fieldWrapper, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                <label style={ps.labelStyle}>Gesetzlicher Mindesturlaub</label>
                <div className={styles.statValue}>{statutory != null ? `${statutory} Tage` : '\u2013'}</div>
              </div>
              <div className="pa-field" style={ps.fieldWrapper}>
                <label style={ps.labelStyle}>Extra Urlaubstage</label>
                <input type="number" style={ps.inputStyle} value={parseDecimalString(profile?.leave?.holiday_extra_days_per_year)} onChange={e => onLeaveChange?.({ ...profile.leave, holiday_extra_days_per_year: toNullableDecimalString(e.target.value) })} step="0.5" min="0" max="40" placeholder="0" />
              </div>
              <div className="pa-field" style={{ ...ps.fieldWrapper, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                <label style={ps.labelStyle}>Gesamt/Jahr</label>
                <div className={styles.statValueMagenta}>{totalLeavePerYear != null ? `${formatDaysHalf(totalLeavePerYear)} Tage` : '\u2013'}</div>
              </div>
            </div>
            {totalLeavePerYear != null && (
              <div className={styles.leaveCalc}>= {statutory ?? 0} gesetzl. + {extra} extra</div>
            )}
            {/* Comp editing */}
            <div className={styles.formGrid2}>
              <div className="pa-field" style={ps.fieldWrapper}>
                <label style={ps.labelStyle}>Verguetungsart</label>
                <div className={styles.compTypeContainer}>
                  <button type="button" onClick={() => onCompFormChange({ ...compForm, type: 'HOURLY' })} className={`${styles.compTypeButton} ${compForm.type === 'HOURLY' ? styles.compTypeActive : styles.compTypeInactive}`}>Stundenlohn</button>
                  <button type="button" onClick={() => onCompFormChange({ ...compForm, type: 'SALARY' })} className={`${styles.compTypeButton} ${compForm.type === 'SALARY' ? styles.compTypeActive : styles.compTypeInactive}`}>Gehalt</button>
                </div>
              </div>
              <div className="pa-field" style={ps.fieldWrapper}>
                <label style={ps.labelStyle}>{compForm.type === 'HOURLY' ? 'Stundenlohn (EUR)' : 'Monatsgehalt (EUR)'}</label>
                <input type="text" inputMode="decimal" style={ps.inputStyle} value={compForm.amount} onChange={e => onCompFormChange({ ...compForm, amount: e.target.value })} onFocus={e => e.target.select()} placeholder={(() => { const curr = compensation?.current; if (!curr) return compForm.type === 'HOURLY' ? '14,00' : '3.200,00'; return curr.compensation_type === 'SALARY' ? ((curr.salary_monthly_cents || 0) / 100).toLocaleString('de-DE', { minimumFractionDigits: 2 }) : parseFloat(curr.hourly_rate || '0').toLocaleString('de-DE', { minimumFractionDigits: 2 }); })()} />
              </div>
            </div>
          </div>
        ) : (
          <div className={styles.contractDetails}>
            <DataRow label="Rolle" value={positionTitle} />
            <DataRow label="Abteilung" value={department} />
            <DataRow label="Typ" value={getEmploymentLabel(employmentType)} />
            <DataRow label="Stunden" value={hoursPerWeek ? `${hoursPerWeek} Std/Woche` : '\u2013'} />
            {/* Arbeitstage visual */}
            {(() => {
              if (wdCountDisplay === 0 && totalLeavePerYear == null) return null;
              return (
                <div>
                  {wdCountDisplay > 0 && (
                    <div className={styles.arbeitstageRow}>
                      <span className={styles.dataRowLabel}>Arbeitstage</span>
                      <div className={styles.dayPills}>
                        {DAY_LABELS.map((day, i) => (
                          <span key={day} className={`${styles.dayPill} ${regularWdDisplay[i] > 0 ? styles.dayPillActive : styles.dayPillInactive}`}>{day}</span>
                        ))}
                      </div>
                    </div>
                  )}
                  <DataRow label="Urlaub" value={totalLeavePerYear != null ? `${formatDaysHalf(totalLeavePerYear)} Tage/Jahr (${statutory ?? 0} + ${extra})` : '\u2013'} />
                </div>
              );
            })()}
            {totalLeavePerYear == null && <DataRow label="Urlaub" value="\u2013" />}
          </div>
        )}

        {/* Pro-rata */}
        {proRata != null && totalLeavePerYear != null && (
          <div className={styles.proRata}>
            &rarr; anteilig ({year}): <span style={{ fontWeight: 500 }}>{formatDaysHalf(totalLeavePerYear)} x {monthsEmployed}/12 = {formatDaysHalf(proRata)} Tage</span>
          </div>
        )}

        <DashedSeparator />

        {/* Leave taken / remaining */}
        <div className={styles.contractDetails}>
          <DataRow label="Genommen" value={leaveTaken != null ? `${formatDaysHalf(leaveTaken)} Tage` : '\u2013'} />
          <DataRow label="Aufgelaufen" value={proRata != null ? `${formatDaysHalf(proRata)} Tage (${monthsEmployed}/12 Mon.)` : '\u2013'} />
          <DataRow label="Verbleibend" value={leaveRemaining != null ? `${formatDaysHalf(leaveRemaining)} Tage` : '\u2013'} highlight />
        </div>

        <DashedSeparator />

        {/* Overtime */}
        <div className={styles.subLabel}>UEBERSTUNDENKONTO</div>
        <div className={styles.contractDetails}>
          <DataRow label="Uebertrag" value={overtimeCarryover != null ? `${overtimeCarryover >= 0 ? '+' : ''}${Number(overtimeCarryover).toLocaleString('de-DE', { minimumFractionDigits: 1 })} Std` : '\u2013'} />
          <DataRow label="Periode" value="\u2013" />
          <DataRow label="Saldo" value={overtimeCarryover != null ? `${overtimeCarryover >= 0 ? '+' : ''}${Number(overtimeCarryover).toLocaleString('de-DE', { minimumFractionDigits: 1 })} Std` : '\u2013'} highlight />
        </div>

        <DashedSeparator />

        {/* Compensation */}
        <div className={styles.compHeader}>
          <div className={styles.subLabel}>VERGUETUNG</div>
          {!compLocked && (
            <button type="button" onClick={() => onSetShowCompForm(!showCompForm)} className={styles.compAddButton}>+ Aenderung</button>
          )}
        </div>

        {compLocked ? (
          <div className={styles.compLocked}>&#x1F512; Gesperrt</div>
        ) : compHistory.length === 0 ? (
          <div className={styles.compEmpty}>Keine Verguetungsdaten</div>
        ) : (
          <div className={styles.compList}>
            {compHistory.map((p: any, i: number) => {
              const isCurrent = compensation?.current?.valid_from === p.valid_from;
              const amountLabel = p.compensation_type === 'SALARY'
                ? `${((p.salary_monthly_cents || 0) / 100).toLocaleString('de-DE', { minimumFractionDigits: 2 })} EUR/Mon.`
                : `${parseFloat(p.hourly_rate || '0').toLocaleString('de-DE', { minimumFractionDigits: 2 })} EUR/h`;
              const amountRaw = p.compensation_type === 'SALARY'
                ? ((p.salary_monthly_cents || 0) / 100).toLocaleString('de-DE', { minimumFractionDigits: 2 })
                : parseFloat(p.hourly_rate || '0').toLocaleString('de-DE', { minimumFractionDigits: 2 });

              return (
                <div
                  key={`${p.valid_from}-${i}`}
                  onClick={() => {
                    if (compLocked) return;
                    onCompFormChange({ type: (p.compensation_type === 'SALARY' ? 'SALARY' : 'HOURLY') as CompensationType, amount: amountRaw, valid_from: p.valid_from || '' });
                    onSetShowCompForm(true);
                  }}
                  className={`${styles.compEntry} ${!compLocked ? styles.compEntryClickable : ''}`}
                >
                  <span className={styles.compDate}>seit {formatDateDE(p.valid_from)}</span>
                  <span className={styles.compAmount} style={{ color: isCurrent ? colors.magenta : colors.navy, fontWeight: isCurrent ? 700 : 500 }}>
                    {amountLabel}
                    {!compLocked && <FaPen style={{ fontSize: 9, opacity: 0.4 }} />}
                  </span>
                </div>
              );
            })}
          </div>
        )}

        {showCompForm && !compLocked && (
          <CompensationForm
            compForm={compForm}
            onCompFormChange={onCompFormChange}
            compSaving={compSaving}
            onSave={onSaveCompensation}
            onCancel={() => { onSetShowCompForm(false); onCompFormChange({ type: 'HOURLY', amount: '', valid_from: '' }); }}
          />
        )}

        {editingContract && (
          <>
            <DashedSeparator />
            <div className={styles.editCancelRow}>
              <span className={styles.editCancelHint}>Aenderungen oben mit "Save Changes" speichern</span>
              <button type="button" className="hover-btn-secondary" onClick={onCancelEditing} style={{ ...ps.buttonSecondary, fontSize: 12, padding: '6px 16px' }}>
                Abbrechen
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export { DataRow, DashedSeparator, formatDateDE, getEmploymentLabel };
